import {
  DEFAULT_CONFIG,
  DIRS,
  OPPOSITE,
  type Cell,
  type Dir,
  type FoodKind,
  type GameState,
  type SimConfig,
  type SimEvent,
} from './constants';

export function sameCell(a: Cell, b: Cell): boolean {
  return a.x === b.x && a.y === b.y;
}

export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function gridForViewport(width: number, height: number): { cols: number; rows: number } {
  const side = Math.min(width, height);
  let n = Math.round(side / 42);
  if (n % 2 === 0) n -= 1;
  n = Math.max(11, Math.min(19, n));
  return { cols: n, rows: n };
}

export function cloneCell(c: Cell): Cell {
  return { x: c.x, y: c.y };
}

function cloneSnake(snake: Cell[]): Cell[] {
  return snake.map(cloneCell);
}

function inBounds(state: GameState, c: Cell): boolean {
  return c.x >= 0 && c.y >= 0 && c.x < state.cols && c.y < state.rows;
}

function occupiedKeys(state: GameState, extra: Cell[] = []): Set<string> {
  const keys = new Set<string>();
  const add = (c: Cell) => keys.add(`${c.x},${c.y}`);
  for (const c of state.snake) add(c);
  for (const c of state.obstacles) add(c);
  add(state.food.cell);
  if (state.boost) add(state.boost.cell);
  if (state.portals) {
    add(state.portals.a);
    add(state.portals.b);
  }
  for (const c of extra) add(c);
  return keys;
}

export function pickEmpty(state: GameState, extra: Cell[] = []): Cell | null {
  const blocked = occupiedKeys(state, extra);
  const free: Cell[] = [];
  for (let y = 0; y < state.rows; y++) {
    for (let x = 0; x < state.cols; x++) {
      if (!blocked.has(`${x},${y}`)) free.push({ x, y });
    }
  }
  if (free.length === 0) return null;
  return free[Math.floor(state.rng() * free.length)]!;
}

function neighbors(c: Cell): Cell[] {
  return [
    { x: c.x + 1, y: c.y },
    { x: c.x - 1, y: c.y },
    { x: c.x, y: c.y + 1 },
    { x: c.x, y: c.y - 1 },
  ];
}

export function canReach(state: GameState, from: Cell, goal: Cell, extraBlocked: Cell[] = []): boolean {
  if (sameCell(from, goal)) return true;
  const blocked = new Set<string>();
  const add = (c: Cell) => blocked.add(`${c.x},${c.y}`);
  for (const c of state.obstacles) add(c);
  for (const c of extraBlocked) add(c);
  for (const c of state.snake) add(c);
  blocked.delete(`${from.x},${from.y}`);
  blocked.delete(`${goal.x},${goal.y}`);

  const seen = new Set<string>([`${from.x},${from.y}`]);
  const q: Cell[] = [from];
  while (q.length) {
    const cur = q.shift()!;
    for (const n of neighbors(cur)) {
      if (!inBounds(state, n)) continue;
      const key = `${n.x},${n.y}`;
      if (seen.has(key) || blocked.has(key)) continue;
      if (n.x === goal.x && n.y === goal.y) return true;
      seen.add(key);
      q.push(n);
    }
  }
  return false;
}

function aheadCells(state: GameState, n: number): Cell[] {
  const out: Cell[] = [];
  const step = DIRS[state.dir];
  let x = state.snake[0]!.x;
  let y = state.snake[0]!.y;
  for (let i = 0; i < n; i++) {
    x += step.x;
    y += step.y;
    if (x < 0 || y < 0 || x >= state.cols || y >= state.rows) break;
    out.push({ x, y });
  }
  return out;
}

function isAdjacent(a: Cell, b: Cell): boolean {
  return Math.abs(a.x - b.x) + Math.abs(a.y - b.y) === 1;
}

function emit(state: GameState, event: SimEvent): void {
  state.events.push(event);
}

export function drainEvents(state: GameState): SimEvent[] {
  const events = state.events;
  state.events = [];
  return events;
}

function spawnFood(state: GameState, kind?: FoodKind): void {
  const cell = pickEmpty(state);
  if (!cell) return;
  const useGolden =
    kind === 'golden' ||
    (kind !== 'red' && state.applesEaten > 0 && state.rng() < state.config.goldenChance);
  state.food = {
    cell,
    kind: useGolden ? 'golden' : 'red',
    ttlMs: useGolden ? state.config.goldenTtlMs : null,
  };
}

function trySpawnBoost(state: GameState): void {
  if (state.boost || state.boostMs > 0) return;
  if (state.snake.length < state.config.boostMinLength) return;
  if (state.rng() >= state.config.boostChance) return;
  const cell = pickEmpty(state);
  if (cell) state.boost = { cell };
}

function trySpawnPortals(state: GameState): void {
  if (state.portals) return;
  if (state.snake.length < state.config.portalMinLength) return;
  if (state.rng() >= state.config.portalChance) return;
  const a = pickEmpty(state);
  if (!a) return;
  const b = pickEmpty(state, [a]);
  if (!b) return;
  const dist = Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
  if (dist < state.config.portalMinDistance) return;
  state.portals = {
    a,
    b,
    ttlMs: state.config.portalTtlMs,
    usesLeft: state.config.portalUses,
  };
}

function tryPlaceObstacles(state: GameState): void {
  if (state.snake.length < state.nextObstacleAtLength) return;
  if (state.obstacles.length >= state.config.obstacleCap) {
    state.nextObstacleAtLength += state.config.obstacleEvery;
    return;
  }

  const forbidden = aheadCells(state, 4);
  const want = Math.min(
    state.config.obstaclesPerWave,
    state.config.obstacleCap - state.obstacles.length,
  );
  let placed = 0;
  for (let attempt = 0; attempt < 40 && placed < want; attempt++) {
    const cell = pickEmpty(state, forbidden);
    if (!cell) break;
    if (isAdjacent(cell, state.food.cell)) continue;
    if (!canReach(state, state.snake[0]!, state.food.cell, [cell])) continue;
    state.obstacles.push(cell);
    placed += 1;
  }
  state.nextObstacleAtLength += state.config.obstacleEvery;
}

export function createGame(
  opts: {
    cols?: number;
    rows?: number;
    rng?: () => number;
    config?: Partial<SimConfig>;
    snake?: Cell[];
    dir?: Dir;
    food?: FoodKind;
    foodCell?: Cell;
  } = {},
): GameState {
  const config = { ...DEFAULT_CONFIG, ...opts.config };
  const cols = opts.cols ?? 17;
  const rows = opts.rows ?? 17;
  const rng = opts.rng ?? mulberry32(Date.now() >>> 0);
  const midY = Math.floor(rows / 2);
  const midX = Math.floor(cols / 2);
  const snake =
    opts.snake ??
    Array.from({ length: config.startLength }, (_, i) => ({
      x: midX - i,
      y: midY,
    }));

  const state: GameState = {
    cols,
    rows,
    snake,
    prevSnake: cloneSnake(snake),
    dir: opts.dir ?? 'right',
    queuedDir: null,
    pendingGrow: 0,
    score: 0,
    food: { cell: { x: 0, y: 0 }, kind: 'red', ttlMs: null },
    boost: null,
    boostMs: 0,
    obstacles: [],
    portals: null,
    portalImmunity: 0,
    applesEaten: 0,
    nextObstacleAtLength: config.firstObstacleLength,
    phase: 'ready',
    death: null,
    baseSpeed: config.startSpeed,
    moveAccumMs: 0,
    rng,
    config,
    events: [],
  };

  if (opts.foodCell) {
    state.food = {
      cell: cloneCell(opts.foodCell),
      kind: opts.food ?? 'red',
      ttlMs: opts.food === 'golden' ? config.goldenTtlMs : null,
    };
  } else {
    spawnFood(state, opts.food ?? 'red');
  }
  return state;
}

export function queueDir(state: GameState, dir: Dir): boolean {
  if (state.phase === 'gameover') return false;
  if (state.snake.length > 1 && OPPOSITE[state.dir] === dir) return false;
  if (state.phase === 'ready') {
    state.dir = dir;
    state.queuedDir = null;
    state.phase = 'playing';
    return true;
  }
  state.queuedDir = dir;
  return true;
}

function onEat(state: GameState, kind: FoodKind): void {
  const boosted = state.boostMs > 0;
  const base = kind === 'golden' ? state.config.goldenScore : state.config.redScore;
  const bonus = boosted ? state.config.boostBonus : 0;
  state.score += base + bonus;
  state.applesEaten += 1;
  state.baseSpeed = Math.min(state.config.maxSpeed, state.baseSpeed + state.config.speedPerApple);
  emit(state, { type: 'eat', kind, bonus: boosted });
  spawnFood(state);
  tryPlaceObstacles(state);
}

function otherPortal(pair: { a: Cell; b: Cell }, cell: Cell): Cell | null {
  if (sameCell(pair.a, cell)) return pair.b;
  if (sameCell(pair.b, cell)) return pair.a;
  return null;
}

function hitsSelf(state: GameState, head: Cell): boolean {
  const ignoreTail = state.pendingGrow === 0;
  const last = state.snake.length - 1;
  for (let i = 0; i < state.snake.length; i++) {
    if (ignoreTail && i === last) continue;
    if (sameCell(state.snake[i]!, head)) return true;
  }
  return false;
}

export function tick(state: GameState): void {
  if (state.phase !== 'playing') return;

  state.prevSnake = cloneSnake(state.snake);

  if (state.queuedDir) {
    if (!(state.snake.length > 1 && OPPOSITE[state.dir] === state.queuedDir)) {
      state.dir = state.queuedDir;
    }
    state.queuedDir = null;
  }

  const step = DIRS[state.dir];
  let head: Cell = { x: state.snake[0]!.x + step.x, y: state.snake[0]!.y + step.y };
  let teleported = false;

  if (state.portals && state.portalImmunity <= 0) {
    const dest = otherPortal(state.portals, head);
    if (dest) {
      head = cloneCell(dest);
      state.portalImmunity = state.config.portalImmunityTicks;
      state.portals.usesLeft -= 1;
      emit(state, { type: 'portal' });
      if (state.portals.usesLeft <= 0) state.portals = null;
      teleported = true;
    }
  } else if (state.portalImmunity > 0) {
    state.portalImmunity -= 1;
  }

  if (!inBounds(state, head)) {
    state.phase = 'gameover';
    state.death = 'wall';
    emit(state, { type: 'die', reason: 'wall' });
    return;
  }

  if (state.obstacles.some((c) => sameCell(c, head))) {
    state.phase = 'gameover';
    state.death = 'obstacle';
    emit(state, { type: 'die', reason: 'obstacle' });
    return;
  }

  const ate = sameCell(head, state.food.cell);
  const gotBoost = state.boost ? sameCell(head, state.boost.cell) : false;
  if (ate) {
    state.pendingGrow += state.food.kind === 'golden' ? state.config.goldenGrow : state.config.redGrow;
  }

  if (hitsSelf(state, head)) {
    state.phase = 'gameover';
    state.death = 'self';
    emit(state, { type: 'die', reason: 'self' });
    return;
  }

  state.snake.unshift(head);
  if (teleported) state.prevSnake[0] = cloneCell(head);
  if (state.pendingGrow > 0) {
    state.pendingGrow -= 1;
  } else {
    state.snake.pop();
  }

  if (ate) onEat(state, state.food.kind);
  if (gotBoost) {
    state.boost = null;
    state.boostMs = state.config.boostMs;
    emit(state, { type: 'boost' });
  }

  trySpawnBoost(state);
  trySpawnPortals(state);
}

export function tickTimers(state: GameState, dtMs: number): void {
  if (state.phase !== 'playing') return;
  if (dtMs <= 0) return;

  if (state.food.kind === 'golden' && state.food.ttlMs !== null) {
    state.food.ttlMs -= dtMs;
    if (state.food.ttlMs <= 0) {
      emit(state, { type: 'golden-gone' });
      spawnFood(state, 'red');
    }
  }

  if (state.boostMs > 0) {
    state.boostMs = Math.max(0, state.boostMs - dtMs);
  }

  if (state.portals) {
    state.portals.ttlMs -= dtMs;
    if (state.portals.ttlMs <= 0) state.portals = null;
  }
}

export function moveIntervalMs(state: GameState): number {
  const speed = state.baseSpeed * (state.boostMs > 0 ? 2 : 1);
  return 1000 / Math.max(0.5, speed);
}

export function step(state: GameState, dtMs: number): void {
  tickTimers(state, dtMs);
  if (state.phase !== 'playing') return;
  state.moveAccumMs += dtMs;
  let guard = 0;
  while (state.phase === 'playing' && state.moveAccumMs >= moveIntervalMs(state) && guard < 4) {
    state.moveAccumMs -= moveIntervalMs(state);
    tick(state);
    guard += 1;
  }
}

export function resetGame(state: GameState): GameState {
  return createGame({
    cols: state.cols,
    rows: state.rows,
    rng: state.rng,
    config: state.config,
  });
}

export function moveProgress(state: GameState): number {
  if (state.phase !== 'playing') return 0;
  const interval = moveIntervalMs(state);
  return Math.min(1, Math.max(0, state.moveAccumMs / interval));
}
